import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useRepo } from "@automerge/react";
import { Link } from "@tanstack/react-router";
import { useAppIndex } from "forest/sandbox/use-app-index";
import { LayoutGrid, Plus } from "lucide-react";
import type { HostPlayground } from "playground/hal/index";
import { createPlayground } from "playground/hal/playground";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { availableJigs } from "./catalog";
import { installJig, jigParseOptions } from "./install";
import { jigWindow } from "./launch";
import { jigRouteParams } from "./routing";

export function ProjectJigs({
  projectId,
  indexUrl,
  onSelect,
}: {
  projectId: string;
  indexUrl: AutomergeUrl;
  onSelect: () => void;
}) {
  const repo = useRepo();
  const [playground, setPlayground] = useState<HostPlayground | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const { entries, loaded } = useAppIndex(playground);
  useEffect(() => {
    let live = true;
    setPlayground(null);
    void createPlayground({ repo, root: indexUrl, apps: jigParseOptions }).then(
      (pg) => {
        if (live) setPlayground(pg);
      },
    );
    return () => {
      live = false;
    };
  }, [repo, indexUrl]);

  return (
    <div className="mt-7">
      <div className="mb-2 px-2.5 text-[10px] font-medium uppercase tracking-[0.13em] text-muted-foreground">
        Jigs
      </div>
      <div className="flex flex-col gap-1">
        {entries
          .filter((entry) => entry.id && jigWindow(entry))
          .map((entry) => (
            <Link
              key={entry.path}
              to="/p/$projectId/$branch/$scope/$appId"
              params={{
                projectId,
                branch: "main",
                ...jigRouteParams(entry.id),
              }}
              activeProps={{
                className:
                  "bg-sidebar-accent font-semibold text-sidebar-foreground",
              }}
              onClick={onSelect}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <LayoutGrid className="size-4 shrink-0" />
              <span className="truncate">{entry.displayName}</span>
            </Link>
          ))}
        {loaded && entries.length === 0 ? (
          <p className="px-2.5 text-[11px] leading-4 text-muted-foreground">
            No jigs yet. Specialized tools for external systems, calculations,
            and documents.
          </p>
        ) : null}
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="justify-start">
              <Plus data-icon="inline-start" />
              Add jigs
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add jigs</DialogTitle>
              <DialogDescription>
                Add specialized tools for calculations and documents to this
                project.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              {availableJigs.map((manifest) => {
                const installed = entries.some(
                  (entry) => entry.id === manifest.name,
                );
                return (
                  <div
                    key={manifest.name}
                    className="flex items-center gap-4 rounded-md border p-4"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{manifest.displayName}</p>
                      <p className="text-sm text-muted-foreground">
                        {manifest.description}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={
                        installed
                          ? `${manifest.displayName} added`
                          : `Add ${manifest.displayName}`
                      }
                      disabled={
                        !loaded || !playground || installed || adding !== null
                      }
                      onClick={async () => {
                        if (!playground) return;
                        setAdding(manifest.name);
                        try {
                          await installJig(playground, manifest);
                        } catch {
                          toast.error("Could not add jig. Please try again.");
                        } finally {
                          setAdding(null);
                        }
                      }}
                    >
                      {installed
                        ? "Added"
                        : adding === manifest.name
                          ? "Adding..."
                          : "Add"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
